import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Users, Gamepad2, MessageCircle, Home, ArrowRight, ArrowLeft, LogOut, User } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'

interface HeaderProps {
  onApplyClick: () => void
}

const Header: React.FC<HeaderProps> = ({ onApplyClick }) => {
  const { isAuthenticated, user, login, logout } = useAuth()

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [activeSection, setActiveSection] = useState('home')
  const [isAccountView, setIsAccountView] = useState<boolean>(typeof window !== 'undefined' ? window.location.hash === '#account' : false)

  useEffect(() => {
    const onHashChange = () => setIsAccountView(window.location.hash === '#account')
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      if (currentScrollY > lastScrollY && currentScrollY > 300) {
        setIsVisible(false)
      } else {
        setIsVisible(true)
      }
      
      setLastScrollY(currentScrollY)

      const sections = ['home', 'about', 'team', 'services']
      const sectionElements = sections.map(id => document.getElementById(id))
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const element = sectionElements[i]
        if (element) {
          const rect = element.getBoundingClientRect()
          if (rect.top <= 100) {
            setActiveSection(sections[i])
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const navItems = [
    { name: 'Главная', href: '#home', icon: Home },
    { name: 'О нас', href: '#about', icon: Users },
    { name: 'Руководство', href: '#team', icon: Gamepad2 },
    { name: 'Сервисы', href: '#services', icon: MessageCircle },
  ]

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ 
        y: isVisible ? 0 : -100,
        opacity: isVisible ? 1 : 0
      }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isAccountView ? (
          <div className="relative flex items-center h-20">
            {/* Back on the left */}
            <div className="flex items-center">
              <motion.button
                onClick={() => { window.location.hash = '' }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 text-gray-200 hover:text-white hover:bg-white/15 flex items-center justify-center"
                aria-label="Назад"
              >
                <ArrowLeft size={18} />
              </motion.button>
            </div>

            {/* Centered Logo */}
            <div className="absolute left-1/2 -translate-x-1/2">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => window.location.reload()}
                className="flex items-center space-x-4 group cursor-pointer"
              >
                <div className="relative">
                  <img 
                    src="/content/hugo_logo.png" 
                    alt="Hugo Family Logo" 
                    className="h-12 w-auto"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-2xl font-bold text-orange-500">
                    HUGO
                  </span>
                  <span className="text-sm font-medium text-gray-300 -mt-1">
                    Family
                  </span>
                </div>
              </motion.div>
            </div>

                         {/* Right controls */}
             <div className="ml-auto flex items-center space-x-3">
               <motion.button
                 onClick={logout}
                 whileHover={{ scale: 1.02 }}
                 whileTap={{ scale: 0.98 }}
                 className="px-4 py-2 rounded-xl border border-white/20 text-gray-200 hover:text-white hover:bg-white/10 backdrop-blur-sm flex items-center space-x-2"
                 title="Выйти"
               >
                 <LogOut size={16} />
                 <span>Выйти</span>
               </motion.button>
             </div>
          </div>
        ) : (
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => window.location.reload()}
              className="flex items-center space-x-4 group cursor-pointer"
            >
              <div className="relative">
                <img 
                  src="/content/hugo_logo.png" 
                  alt="Hugo Family Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-orange-500">
                  HUGO
                </span>
                <span className="text-sm font-medium text-gray-300 -mt-1">
                  Family
                </span>
              </div>
            </motion.div>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center space-x-3">
              {navItems.map((item) => {
                const isActive = activeSection === item.href.replace('#', '')
                return (
                  <motion.a
                    key={item.name}
                    href={item.href}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                      isActive 
                        ? 'text-orange-400 bg-white/10 backdrop-blur-sm' 
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="flex items-center space-x-2">
                      <item.icon size={16} />
                      <span>{item.name}</span>
                    </span>
                  </motion.a>
                )
              })}
            </nav>

            {/* Right Controls */}
            <div className="hidden lg:flex items-center space-x-3">
              <motion.button
                onClick={onApplyClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="items-center space-x-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-500/25 relative overflow-hidden group hidden md:flex"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                <ArrowRight size={16} className="relative z-10" />
                <span className="relative z-10">Подать заявку</span>
              </motion.button>
              
                             {isAuthenticated ? (
                 <div className="flex items-center space-x-3">
                   <motion.button
                     onClick={() => window.location.hash = '#account'}
                     whileHover={{ scale: 1.02 }}
                     whileTap={{ scale: 0.98 }}
                     className="flex items-center space-x-2 px-4 py-2 rounded-xl border border-white/20 text-gray-200 hover:text-white hover:bg-white/10 transition-all duration-300"
                     title="Перейти в личный кабинет"
                   >
                     {user?.avatar ? (
                       <img 
                         src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`} 
                         alt={user.username}
                         className="w-6 h-6 rounded-full"
                         onError={(e) => {
                           e.currentTarget.style.display = 'none'
                           e.currentTarget.nextElementSibling?.classList.remove('hidden')
                         }}
                       />
                     ) : (
                       <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold hidden">
                         {user?.username?.charAt(0).toUpperCase() || 'U'}
                       </div>
                     )}
                     <span className="text-sm font-medium">{user?.global_name || user?.username}</span>
                     <User size={14} className="text-gray-400" />
                   </motion.button>
                  <motion.button
                    onClick={logout}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-3 py-2 rounded-xl border border-white/20 text-gray-200 hover:text-white hover:bg-white/10 backdrop-blur-sm"
                    title="Выйти"
                  >
                    <LogOut size={16} />
                  </motion.button>
                </div>
              ) : (
                <motion.button
                  onClick={login}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-4 py-2 rounded-xl border border-white/20 text-gray-200 hover:text-white hover:bg-white/10 backdrop-blur-sm"
                >
                  Войти через Discord
                </motion.button>
              )}
            </div>

            {/* Mobile Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="lg:hidden relative w-12 h-12 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 text-gray-300 hover:text-white transition-all duration-300 flex items-center justify-center group"
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative z-10"
                  >
                    <X size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="relative z-10"
                  >
                    <Menu size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        )}

        {/* Mobile Menu */}
        <AnimatePresence>
          {!isAccountView && isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-6 border-t border-white/10 bg-black/20 backdrop-blur-xl">
                <nav className="flex flex-col space-y-3">
                  {navItems.map((item) => {
                    const isActive = activeSection === item.href.replace('#', '')
                    return (
                      <motion.a
                        key={item.name}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        whileHover={{ x: 10 }}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                          isActive 
                            ? 'text-orange-400 bg-white/10 backdrop-blur-sm' 
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <item.icon size={18} />
                        <span>{item.name}</span>
                        {isActive && (
                          <motion.div
                            layoutId="mobileActiveTab"
                            className="w-2 h-2 bg-orange-400 rounded-full ml-auto"
                            initial={false}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        )}
                      </motion.a>
                    )
                  })}
                                     {/* Mobile auth button */}
                   {isAuthenticated ? (
                     <div className="space-y-3">
                       <motion.button
                         onClick={() => {
                           window.location.hash = '#account'
                           setIsMobileMenuOpen(false)
                         }}
                         whileHover={{ scale: 1.02 }}
                         whileTap={{ scale: 0.98 }}
                         className="flex items-center space-x-3 px-4 py-3 rounded-xl bg-white/10 w-full text-left"
                       >
                         {user?.avatar ? (
                           <img 
                             src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`} 
                             alt={user.username}
                             className="w-8 h-8 rounded-full"
                             onError={(e) => {
                               e.currentTarget.style.display = 'none'
                               e.currentTarget.nextElementSibling?.classList.remove('hidden')
                             }}
                           />
                         ) : (
                           <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold hidden">
                             {user?.username?.charAt(0).toUpperCase() || 'U'}
                           </div>
                         )}
                         <div className="flex-1">
                           <div className="text-white font-medium">{user?.global_name || user?.username}</div>
                           <div className="text-gray-300 text-sm">@{user?.username}</div>
                         </div>
                       </motion.button>
                      <motion.button
                        onClick={() => {
                          logout()
                          setIsMobileMenuOpen(false)
                        }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex items-center justify-center space-x-2 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 bg-red-500/20 border border-red-500/30 w-full"
                      >
                        <LogOut size={16} />
                        <span>Выйти</span>
                      </motion.button>
                    </div>
                  ) : (
                    <motion.button
                      onClick={() => {
                        login()
                        setIsMobileMenuOpen(false)
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center justify-center space-x-2 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 bg-white/10 w-full"
                    >
                      Войти через Discord
                    </motion.button>
                  )}
                  <motion.button
                    onClick={() => {
                      onApplyClick()
                      setIsMobileMenuOpen(false)
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="mt-4 flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-orange-500/25 relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                    <ArrowRight size={16} className="relative z-10" />
                    <span className="relative z-10">Подать заявку</span>
                  </motion.button>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  )
}

export default Header 